if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.searchModalOpen = false;
      this.searchUrl = this.dataset.searchUrl;
      this.searchQuery = "";
    }

    connectedCallback() {
      this.predictiveSearchWrapper = this.querySelector('[data-predictive-search-wrapper]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.searchQuery = this.searchInput.value;
      this.searchDropdown = this.querySelector('[data-search-dropdown]');
      this.clearSearch = this.querySelector('[data-clear-search]');
      this.openSearch = this.querySelector('[data-open-search]');
      this.klaviyoID = __isp_get_customer_identifier_from_klaviyo();
      
      this.addKeydownListener();
      this.addDelegatedClickListeners();
      window.FastSimonSDK.getAllPopularProducts().then(r => {
        this.popularProducts = r?.slice(0, 10);  
        this.popularSuggestions = r?.slice(10, 15);
      });
      
      this.fetchPredictiveResults();
    }

    addDelegatedClickListeners() {
      this.addEventListener('submit', (e) => {
        e.preventDefault();
        const redirectLink = this.suggestedLinks.find(({ l }) => l?.toLowerCase().trim() === this.searchQuery?.toLowerCase().trim());
        if (!redirectLink) {
          e.target.submit();
          return;
        }
        
        const relativeUrl = new URL(redirectLink.u, window.location.origin).pathname;
        window.location.href = relativeUrl;
      });

      this.addEventListener('click', (e) => {
        const anchor = e.target.closest('[href]');
        
        if (anchor?.dataset.popularProduct) {
          e.preventDefault();
          window.FastSimonSDK.event({
            eventName: window.FastSimonEventName.AutocompletePopularClicked,
            data: {
              query: this.searchQuery, // (Required)
              term: anchor.innerText // (Required)
            }
          });
          
          window.location.href = anchor.href;
          return;
        }
        
        if (anchor && anchor.dataset.fsId && anchor.dataset.fsS) {
          e.preventDefault();
          window.FastSimonSDK.event({
            eventName: window.FastSimonEventName.AutocompleteProductClicked,
            data: {
              kl_id: this.klaviyoID,
              query: this.searchQuery,
              productID: anchor.dataset.fsId,
              productQuery: anchor.dataset.fsS
            }
          });
          
          window.location.href = anchor.href;
          return;
        };
        
        if (anchor && anchor.dataset.collectionId) {
          e.preventDefault();
          window.FastSimonSDK.event({
            eventName: window.FastSimonEventName.AutocompleteCategoryClicked,
            data: {
              kl_id: this.klaviyoID,
              query: this.searchQuery,
              collectionID: anchor.dataset.collectionID,
            }
         });
         
         window.location.href = anchor.href;
         return;
        };        
      });

      window.addEventListener('click', debounce(async (e) => {
        if (e.target.closest('[data-clear-search]')) {
          this.searchInput.value = '';
          this.searchQuery = '';
          await this.fetchPredictiveResults();
          this.hideClearSearchResults();
          return;
        }
      }, 10));
      this.searchInput.addEventListener(`focus`, function() {
        this.focus();
        this.setSelectionRange(this.value.length, this.value.length);
      });
    }

    addKeydownListener() {
      this.searchInput.addEventListener('input', debounce(async (e) => {
        this.searchQuery = this.searchInput.value;
        if (!e.target.value) {
          this.searchInput.value = '';
          this.searchQuery = '';
          this.hideClearSearchResults();
          this.updateSearchResults();
          return;
        }
        this.showClearSearchResults();
        await this.fetchPredictiveResults();
      }, 10));
    }
    
    updateSearchResults(results) {   
      let productsToRender = results,
          suggestions = true,
          products = true;   
      
      this.predictiveSearchWrapper.innerHTML = `
        <div 
          class="
            flex 
            flex-col 
            w-full 
            [&:not(:has(.results_li))_.no-results]:block 
            [&:not(:has(.results_li))_.results]:hidden 
            pb-6
          "
        >
          <span class="results block text-left pb-3 tracking-[0.7px] font-heading text-lg text-chicago uppercase">
            Suggestions
          </span>
          <ul class="flex flex-col gap-4 w-full results">
            ${this.searchQuery.length ? this.suggestedLinks?.slice(0, 5).map((suggestion) => suggestion && `
              <li>
                <a 
                  class="hover-link"
                ${suggestions ? '' : 'data-popular-product'}
                ${suggestion.id ? `data-collection-id="${suggestion.id}" ` : ''}
                  href="${suggestion.u || `/search?q=${suggestion.label}`}"
                >
                  ${suggestion.l || suggestion.label}
                </a>
              </li>
            `).join('') : ``}
          </ul>
          ${this.searchQuery ? `<span class="hidden no-results-message p1 text-chicago pb-4">There are no suggestions that match your entry</span>` : ''}
          <span class="hidden no-results text-left pb-3 tracking-[0.7px] font-heading text-lg text-chicago uppercase">
            Popular Searches
          </span>
          <div class="hidden no-results">
            <ul class="flex flex-col gap-4 w-full">
              ${this.popularSuggestions?.map((suggestion) => suggestion && `
                <li>
                  <a 
                    class="hover-link"
                  ${suggestions ? '' : 'data-popular-product'}
                  ${suggestion.id ? `data-collection-id="${suggestion.id}" ` : ''}
                    href="${suggestion.u || `/search?q=${suggestion.label}`}"
                  >
                    ${suggestion.l || suggestion.label}
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
        <div class="w-full">
          ${productsToRender.length && this.searchQuery.length ?
            `
            <div class="flex flex-col justify-between">
              <span class="block text-left pb-3 tracking-[0.7px] font-heading text-lg text-chicago uppercase">
                Products
              </span>
              <ul class="flex flex-col gap-2">
              ${productsToRender.map((product) => product && `
                <li>
                  <a 
                    ${products ? '' : 'data-popular-product'} 
                    data-fs-s="${product.s}" 
                    data-fs-id="${product.id}" 
                    class="product-result" 
                    href="${product.u}"
                  >
                    <intersection-loader 
                      data-url="${product.u}" 
                      data-section-id="predictive-search-tile"
                      class="w-full aspect-[339/80] loading relative flex items-center justify-center"
                    >
                      <div class="loader">
                        <span class="block w-12 h-12">
                          <svg class="animate-spin -ml-1 mr-3 h-full w-full text-accent1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      </div>
                    </intersection-loader>
                  </a>
                </li>
              `).join('')}
              </ul>
            </div>
            `
            : `
            <div class="flex flex-col justify-between">
              <span class="block text-left pb-3 tracking-[0.7px] font-heading text-lg text-chicago uppercase">
                Popular Products
              </span>
              <ul class="flex flex-col gap-2">
              ${this.popularProducts.map((product) => product && `
                <li>
                  <a 
                    ${products ? '' : 'data-popular-product'} 
                    data-fs-s="${product.s}" 
                    data-fs-id="${product.id}" 
                    class="" 
                    href="${product.u}"
                  >
                    <intersection-loader 
                      data-url="${product.u}" 
                      data-section-id="predictive-search-tile"
                      class="w-full aspect-[339/80] loading relative flex items-center justify-center"
                    >
                      <div class="loader">
                        <span class="block w-12 h-12">
                          <svg class="animate-spin -ml-1 mr-3 h-full w-full text-accent1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      </div>
                    </intersection-loader>
                  </a>
                </li>
              `).join('')}
              </ul>
            </div>
            `
          }
        </div>
      `;
    }

    showClearSearchResults() {
      this.clearSearch.classList.replace('hidden', `block`);
    }
    hideClearSearchResults() {
      this.clearSearch.classList.replace('block', `hidden`);
    }

    async fetchPredictiveResults() {
      if (!window.FastSimonSDK) return;
      return new Promise((resolve, reject) => {
        window.FastSimonSDK.instantSearch({
          query: this.searchQuery,
          callback: (response) => {
            if(!response.payload) {
              reject('Unable to fetch search results.');
              return;
            }
                                      
            const suggestions = [...response.payload.turbolinks, ...response.payload.popularSearches, ...response.payload.categories];
            this.suggestedLinks = suggestions;
            this.updateSearchResults(response.payload.products, this.searchQuery);
            resolve();
          }
        });
      })
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
}